import { useCallback, useMemo, useState } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  isVendorTaxonomyKey as isSearchableVendorTaxonomyKey,
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';

import { EditCategoryModalProps } from '~/components/common/modals/EditCategoryModal/EditCategoryModal';
import { useModal } from '~/contexts/ModalContext';
import { SharedPageTitle } from '~/pages/couples/explore/components/SearchResults/components/SharedPageTitle/SharedPageTitle';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { ALL_MARKETS, isSearchLocation } from '~/util/searchUtils';

import { PageSection } from '../../../PageSection/PageSection';
import SaveNewSearchButton from '../../../SavedSearches/SaveNewSearchButton';
import { InlineSearchBar } from '../../../SearchBar/InlineSearchBar';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import { PartialTaxonomyNode } from '../../types/types';
import { getCanonicalFacetDescription } from '../../utils/canonicalFacetHelpers';
import { FeaturedVendorsToolTip } from '../FeaturedVendorsToolTip/FeaturedVendorsToolTip';

import styles from './pageHeading.module.less';

const categoryHeadings = getVendorTaxonomySwitchFunc<SearchableVendorTaxonomyKey, string>({
  [BANDS_DJS_TAXONOMY_KEY]: 'Bands and DJs',
  [BAR_SERVICES_TAXONOMY_KEY]: 'Bar Services & Beverages',
  [CAKES_DESSERTS_TAXONOMY_KEY]: 'Cakes & Desserts',
  [CATERING_TAXONOMY_KEY]: 'Caterers',
  [FLORISTS_TAXONOMY_KEY]: 'Florists',
  [HAIR_MAKEUP_TAXONOMY_KEY]: 'Beauty Professionals',
  [PHOTOGRAPHERS_TAXONOMY_KEY]: 'Photographers',
  [PLANNERS_TAXONOMY_KEY]: 'Planners',
  [VENUES_TAXONOMY_KEY]: 'Venues',
  [VIDEOGRAPHERS_TAXONOMY_KEY]: 'Videographers',
});

export const getCategoryHeading = (selectedTaxonomy: VendorTaxonomyKey | undefined) => {
  if (isSearchableVendorTaxonomyKey(selectedTaxonomy)) {
    return categoryHeadings(selectedTaxonomy);
  }
  return 'Vendors';
};

const PageTitle = ({
  updateSelectedLocation,
  updateTaxonomy,
}: {
  updateSelectedLocation: (newLocation: LocationOrAllMarketsType) => void;
  updateTaxonomy: (newCategory: PartialTaxonomyNode | null) => void;
}) => {
  const { showV2PredefinedModal } = useModal();
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedTaxonomy, selectedLocation, selectedFacets } = vendorSearch;
  const [showLocationModal, setShowLocationModal] = useState(false);

  const description = useMemo(() => {
    const facetDescription =
      getCanonicalFacetDescription(selectedTaxonomy?.key, selectedFacets, true) || '';
    return `${facetDescription} Wedding`.trim();
  }, [selectedFacets, selectedTaxonomy?.key]);

  const category = useMemo(
    () => getCategoryHeading(selectedTaxonomy?.key),
    [selectedTaxonomy?.key]
  );

  const location = useMemo(
    () => (isSearchLocation(selectedLocation) ? selectedLocation.label : undefined),
    [selectedLocation]
  );

  const onCategoryClick = () => {
    showV2PredefinedModal<EditCategoryModalProps>(
      'EDIT_CATEGORY_MODAL',
      {
        dialogHeadingId: 'edit-category-heading',
        triggerRef: null,
        lockBgScrolling: true,
      },
      {
        onSelect: updateTaxonomy,
      }
    );
  };

  const onLocationClick = () => {
    setShowLocationModal(true);
  };

  return (
    <SharedPageTitle
      category={category}
      description={description}
      location={location}
      onCategoryClick={onCategoryClick}
      onLocationClick={onLocationClick}
      onUpdateSelectedLocation={updateSelectedLocation}
      setShowLocationModal={setShowLocationModal}
      showLocationModal={showLocationModal}
    />
  );
};

const SavedSearch = () => {
  const { vendorSearch, selectedFacetOptions } = useSearchResultsPageContext();
  const { availabilityDates } = vendorSearch;

  return (
    <SaveNewSearchButton
      className={styles.saveSearch}
      vendorSearch={vendorSearch}
      selectedFacetOptions={selectedFacetOptions}
      availabilityDates={availabilityDates}
      component="link"
    />
  );
};

export const PageHeading = () => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedLocation, selectedTaxonomy } = vendorSearch;

  const { navigateToSrp } = useVendorSearchNavigation();

  // Navigate on change if the location is cleared
  const updateSelectedLocation = useCallback(
    (newLocation: LocationOrAllMarketsType) => {
      navigateToSrp({
        selectedLocation: newLocation,
      });
    },
    [navigateToSrp]
  );

  // Navigate on change if the category is cleared
  const updateTaxonomy = useCallback(
    (newCategory: PartialTaxonomyNode | null) => {
      navigateToSrp({
        selectedTaxonomy: newCategory,
      });
    },
    [navigateToSrp]
  );

  return (
    <PageSection className={styles.wrapper} noPadding stretchToEdge>
      <div className={styles.content}>
        <div className={styles.titleWrapper}>
          <PageTitle
            updateSelectedLocation={updateSelectedLocation}
            updateTaxonomy={updateTaxonomy}
          />
          <FeaturedVendorsToolTip />
        </div>
        <div className={styles.searchBarWrapper}>
          <InlineSearchBar
            className={styles.desktopSearchBar}
            selectedLocation={selectedLocation || ALL_MARKETS}
            allowInteractiveSearch
            updateSelectedLocation={updateSelectedLocation}
            selectedTaxonomyNode={selectedTaxonomy ?? null}
            setSelectedTaxonomyNode={updateTaxonomy}
            navigateToSrp={navigateToSrp}
            variant="white"
            reverse
            short
            searchIcon
          />
          <SavedSearch />
        </div>
      </div>
    </PageSection>
  );
};
